import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceStatus,
  IExperience,
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { Button, DialogActions, DialogContent } from '@material-ui/core';
import { useMemo } from 'react';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../../../components/AdditionalActionsMenu';
import { ButtonWithPromise } from '../../../../../../../components/ButtonWithPromise';
import { DeletionConfirmationDialogBody } from '../../../../../../../components/DeletionConfirmation';
import {
  ID_TO_ROW_KEY,
  ItemSorters,
  RowsRenderer,
  RowsRendererScrollContainer,
  ROW_HEIGHTS,
  useSortQueryParam
} from '../../../../../../../components/GroupableList';
import { Loader } from '../../../../../../../components/Loader';
import { IColumn } from '../../../../../../../components/Table/VirtualizedSortable';
import { InstanceStatusChip } from '../../../../../../../features/Experiences/components/InstanceStatusChip';
import { useErrorLogger } from '../../../../../../../hooks/useErrorLogger';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { Section } from '../../../../../../../layout/Section';
import {
  getParticipantActor,
  toStandaloneAppUrl
} from '../../../../../../../services/experiences/instance';
import { removeDoc } from '../../../../../../../services/firebase';
import {
  formatDatePrecise,
  toMoment
} from '../../../../../../../services/time';
import { JsonDialogInner } from '../../../../../../components/Json';
import { useExperienceInstancesByTemplateId } from '../../../../../../services/experiences';
import { toFirestoreConsoleFromDoc } from '../../../../../../services/firebase';
import { publishTopic } from '../../../../../../services/pubsub';
import { ExperienceInstanceWithoutTemplate } from '../../../../../Experiences/components/ExperienceInstance';

type ColumnName = 'expId' | 'userId' | 'status' | 'startsAt' | 'actions';

type D = Doc<IExperience>;
type Column = IColumn<D, ColumnName>;

const Actions = ({ d }: { d: D }) => {
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return [
      {
        key: 'openInFirestore',
        label: 'Open in Firestore console',
        externalHref: toFirestoreConsoleFromDoc(d)
      },
      {
        key: 'inspectRaw',
        label: 'Inspect (raw)...',
        dialog: ({ onClose }) => <JsonDialogInner onClose={onClose} data={d} />
      },
      {
        key: 'inspect',
        label: 'Inspect...',
        dialog: ({ onClose }) => (
          <>
            <DialogContent>
              <ExperienceInstanceWithoutTemplate exp={d} />
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
          </>
        )
      },
      {
        key: 'remove',
        label: 'Remove',
        dialog: ({ onClose }) => {
          return (
            <DeletionConfirmationDialogBody
              onClose={onClose}
              onConfirm={() => removeDoc(d)}
              keyword={d.id}
              dialogTitle={'Remove instance?'}
              buttonAction={'Remove instance'}
            />
          );
        },
        dialogMaxWidth: 'sm',
        dialogFullWidth: true
      }
    ];
  }, [d]);
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'expId',
    head: () => 'Exp ID',
    cell: (d) => d.id,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'userId',
    head: () => 'User',
    cell: (d) => getParticipantActor(d)?.userId,
    align: 'left',
    sortable: false,
    defaultDirection: 'desc',
    width: 180,
    flexGrow: 1
  },
  {
    key: 'status',
    head: () => 'Status',
    cell: (d) => <InstanceStatusChip status={d.data.status} />,
    align: 'left',
    sortable: false,
    defaultDirection: 'desc',
    width: 120,
    flexGrow: 1
  },
  {
    key: 'startsAt',
    head: () => 'startsAt',
    cell: (d) => formatDatePrecise(toMoment(d.data.startsAt)),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 150,
    flexGrow: 0
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d) => <Actions d={d} />,
    sortable: false,
    align: 'right',
    width: 50,
    flexGrow: 0
  }
];

const SORTERS: ItemSorters<D> = {
  startsAt: {
    key: 'startsAt',
    items: { sort: (d) => d.data.startsAt.toMillis(), dir: 'desc' }
  }
};

const DEFAULT_SORTER = SORTERS.startsAt;

export const Instances = ({ tpl }: { tpl: Doc<IExperienceTemplate> }) => {
  const [ds, loading, error] = useExperienceInstancesByTemplateId(tpl.id);
  const [[sorter, dir], setSort] = useSortQueryParam('sort-instances', SORTERS);

  useErrorLogger('instances by templateId', error);
  console.log('instances', ds);
  if (ds) {
    console.log(
      'late:',
      ds.filter((x) => x.data.status === ExperienceStatus.LATE).length
    );
  }

  return (
    <Section>
      <CanvasBar>
        <div>Instances {ds && `(${ds.length})`}</div>
        <ButtonWithPromise
          size="small"
          variant="contained"
          color="primary"
          pending="Starting check..."
          onClick={() =>
            publishTopic({
              topic: 'exp-experiences-checkForLatenessInOrg',
              payload: { ts: Date.now(), orgId: tpl.data.orgId }
            })
          }
        >
          Check for lateness
        </ButtonWithPromise>
      </CanvasBar>
      {loading && <Loader height={500} />}
      {ds && (
        <RowsRendererScrollContainer height={500}>
          <RowsRenderer
            variant="contained"
            columns={COLUMNS}
            rows={ds}
            rowToKey={ID_TO_ROW_KEY}
            rowToHrefExternal={(d) =>
              toStandaloneAppUrl(d, PARTICIPANT_ACTOR_KEY)
            }
            sorter={sorter || DEFAULT_SORTER}
            sortDirection={dir}
            renderHead={true}
            headProps={{ sticky: true }}
            onHeadClick={(c, d) =>
              setSort([SORTERS[c.key] || DEFAULT_SORTER, d])
            }
            chunkSize={30}
            rootMargin="300px"
            rowHeight={ROW_HEIGHTS.dense}
            otherProps={undefined}
          />
        </RowsRendererScrollContainer>
      )}
    </Section>
  );
};
